// Lib
import { isEmpty, assign, union } from 'lodash';

// Util
import { http } from '../utils/services/http';
import { getElement } from '../../common/elements/utils/elementTraversalUtils';
import { isSkeleton } from '../../common/elements/utils/elementTypeUtils';
import { asyncResource } from '../utils/services/http/asyncResource/asyncResource';
import { getDataExpiryTimestamp } from '../utils/services/http/axiosResponseUtils';
import { getTokenElementBatches } from '../utils/services/http/elementRequestTokenUtils';

// Actions
import { loadElements } from './actions/elementActions';
import { commentsLoad } from './comment/store/commentActions';
import { getTokenElementIdGroupsThunk } from '../utils/permissions/permissionsActions';

// State selectors
import { getElements } from './selectors/elementSelector';

// Constants
import { ResourceTypes } from '../utils/services/http/asyncResource/asyncResourceConstants';
import { TIMES } from '../../common/utils/timeUtil';
import { MNComment } from '../../common/comments/commentTypes';
import { MNElement } from '../../common/elements/elementModelTypes';
import { createDebouncedActionDispatcher } from '../utils/services/reduxActionDispatcherUtils';

type FetchElementsArgs = {
    elementIds: string[];
    force?: boolean;
    loadAncestors?: boolean;
    permissionIdsOverride?: Array<string | undefined>;
};

export type FetchElementsResponse = {
    comments: Record<string, MNComment>;
    elements: Record<string, MNElement>;
    elementCount: number;
    errors: Record<string, any>;
    ids: string[];
    asyncResource: {
        errors: Record<string, any>;
        expiry?: number;
    };
};

/**
 * Fetch multiple elements from the server.
 */
export const fetchElements =
    (args: FetchElementsArgs) =>
    async (dispatch: Function): Promise<FetchElementsResponse> =>
        dispatch(
            asyncResource(
                ResourceTypes.elements,
                args.elementIds,
                args.force,
            )(async (elementIdsToFetch) => {
                const { loadAncestors, permissionIdsOverride } = args;

                const tokenGroups = await dispatch(
                    getTokenElementIdGroupsThunk({ elementIds: elementIdsToFetch, permissionIdsOverride }),
                );
                const batches = getTokenElementBatches(tokenGroups);

                const responses = await Promise.all(
                    batches.map((batch) =>
                        http({
                            url: 'elements',
                            timeout: 30 * TIMES.SECOND,
                            params: {
                                loadAncestors,
                                tokens: batch.token || undefined,
                                ids: batch.elementIds.join(','),
                            },
                        }),
                    ),
                );

                let commentsMap = {};
                let elementsMap = {};
                let errorsMap = {};
                let count = 0;
                let allIds: string[] = [];

                let expiry: number | undefined = undefined;

                responses.forEach((response) => {
                    const { data = {} } = response;
                    const { comments = {}, elements = {}, elementCount = 0, errors = {}, ids = [] } = data;

                    commentsMap = assign(commentsMap, comments);
                    elementsMap = assign(elementsMap, elements);
                    errorsMap = assign(errorsMap, errors);
                    count += elementCount;
                    allIds = union(allIds, ids);

                    const responseExpiry = getDataExpiryTimestamp(response);

                    if (responseExpiry && (!expiry || responseExpiry < expiry)) {
                        expiry = responseExpiry;
                    }
                });

                !isEmpty(elementsMap) && dispatch(loadElements(elementsMap));

                !isEmpty(commentsMap) && dispatch(commentsLoad({ comments: commentsMap }));

                return {
                    comments: commentsMap,
                    elements: elementsMap,
                    elementCount: count,
                    errors: errorsMap,
                    ids: allIds,
                    asyncResource: {
                        errors: errorsMap,
                        expiry,
                    },
                };
            }),
        );

/**
 * Merges the arguments for each invocation of the debounced fetch elements.
 * It ensures that:
 * - Element IDs are unique.
 * - The `force` flag is set to true if any of the invocations requested it.
 * - The `loadAncestors` flag is set to true if any of the invocations requested it.
 * - The `permissionIdsOverride` is combined correctly, ensuring that the order of element IDs
 *      matches the unique element IDs.
 */
const mergeDebouncedArgs = (
    aTuple: Readonly<Parameters<typeof fetchElements>>,
    bTuple: Readonly<Parameters<typeof fetchElements>>,
): Parameters<typeof fetchElements> => {
    const a = aTuple[0];
    const b = bTuple[0];

    if (!a && !b) return [{} as FetchElementsArgs];
    if (!b) return [a];
    if (!a) return [b];

    const elementIds = [...a.elementIds, ...b.elementIds];
    const force = a.force || b.force;
    const loadAncestors = a.loadAncestors || b.loadAncestors;

    // The permission ID override relies on the index of the element IDs,
    //  so if we're not overriding, we add an undefined for the element ID's index
    const permissionIdsOverride = [
        ...(a.permissionIdsOverride || a.elementIds.map(() => undefined)),
        ...(b.permissionIdsOverride || b.elementIds.map(() => undefined)),
    ];

    const elementIdsToPermissionIds = elementIds.reduce(
        (acc, elementId, index) => {
            const permissionId = permissionIdsOverride[index];
            acc[elementId] = acc[elementId] || permissionId;
            return acc;
        },
        {} as Record<string, string | undefined>,
    );

    const uniqueElementIds = Object.keys(elementIdsToPermissionIds);
    const orderedPermissionIdsOverride = uniqueElementIds.map((id) => elementIdsToPermissionIds[id]);

    return [
        {
            elementIds: uniqueElementIds,
            force,
            loadAncestors,
            permissionIdsOverride: orderedPermissionIdsOverride,
        },
    ];
};

/**
 * Performs a debounced fetch of multiple elements - waiting for a short period
 * and collecting all the element IDs to fetch before making the request.
 *
 * This is useful for scenarios where multiple fetches might be triggered in quick succession.
 */
export const debouncedFetchElements = createDebouncedActionDispatcher<
    FetchElementsResponse,
    Parameters<typeof fetchElements>
>(fetchElements, { mergeArgs: mergeDebouncedArgs });

/**
 * Similar to fetchElements however if any of the elements are skeletons it will force fetch them.
 */
// FIXME-PERMISSIONS - This is a bit of a hack to get around the issue when users are currently viewing
//  boards that this user has already visited with a permissionId. A better way might be to pre-fetch
//  permission IDs.
export const fetchElementsForceSkeletons =
    (args: FetchElementsArgs) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const { elementIds, force } = args;

        if (force) return dispatch(fetchElements(args));

        const state = getState();

        const elements = getElements(state);

        const overriddenForce = elementIds.some((elementId) => {
            const element = getElement(elements, elementId);
            return isSkeleton(element);
        });

        return dispatch(fetchElements({ ...args, force: overriddenForce }));
    };
